// appBatches — lista batchy i strona jednego batcha (P03, canvas 4 „Batch 100”).
// Klucze lustrzane z ../en/appBatches.ts.
//
// Do tej pory te 33 napisy szły przez `lib/tFallback` z angielskimi tekstami
// wpisanymi w komponent — czyli polski użytkownik widział angielski, a lint
// i testy parytetu nie miały czego złapać, bo tych kluczy nie było w żadnym
// słowniku. To jest ta sama rodzina co gołe klucze: ekran „działa” i jest zły.
// Po tej dostawie `tx()` znajduje klucz i fallback przestaje być używany;
// zostaje jako druga linia obrony, nie jako źródło tekstu.
//
// LICZEBNIKI: `tx()` nie wybiera formy liczby mnogiej, a `requested_count`
// (2–10) i `tokens_charged` trafiają tam surowe. Dlatego tam, gdzie liczba
// stoi przy rzeczowniku, rzeczownik idzie PRZED nią („editów: {n}”) — forma
// poprawna dla każdej wartości bez zmiany w kodzie P03.
export const appBatches = {
  // Przełącznik nad listą (0016/B2, minimum do paczki 5): Serie = ten ekran, Wszystkie edity = /library.
  mode: { label: 'Widok', series: 'Serie', all: 'Wszystkie edity' },
  // Etykieta pobrania całej serii. Zdania objaśniające stany ZIP-a wyleciały
  // zgodnie z zasadą 8; stan pokazuje aktywność akcji.
  archive: {
    download: 'Pobierz wszystkie ({n})',
    // Paczka N2 (Z1): powody, dla których archiwum nie powstanie. Nie stoją jako zdanie
    // na ekranie - idą do czytnika (`aria-describedby`) i do `title` nieaktywnego
    // przycisku, bo martwa kontrolka bez powodu jest gorsza niż jej brak.
    nothingYet: 'Nic się jeszcze nie wyrenderowało.',
    tooLarge: 'Ta seria jest za duża, żeby wysłać ją jednym plikiem - pobierz edity pojedynczo.',
    sizeUnknown: 'Nie umiemy teraz policzyć rozmiaru archiwum - pobierz edity pojedynczo.',
  },
  autopilot: {
    start: 'Autopilot',
    needsOne: 'Autopilot potrzebuje przynajmniej jednego gotowego edita.',
  },
  loading: 'Ładowanie serii…',
  error: 'Nie udało się wczytać Twoich serii.',
  retry: 'Spróbuj ponownie',

  // ── R5 (20.09, reguła 30): TRZY słowa stanu renderu ────────────────────────
  // Domi: „za dużo statusów renderów (…) Gotowe / w kolejce / Błąd”. Kolejka i render
  // to JEDNO słowo („w kolejce”); rozróżnia je wyłącznie pasek postępu na kaflu.
  // „renderuje się” i „nie wyszedł / nie wyszły” znikają z chipów, pigułek i filtrów.
  // Status całego batcha. `partial` istnieje, bo „gotowy” przy części nieudanych
  // renderów byłby nieprawdą; `running` mówi to samo co `queued`, bo w słowniku
  // produktu kolejka i render są jednym stanem.
  status: {
    queued: 'w kolejce',
    running: 'w kolejce',
    done: 'gotowy',
    partial: 'częściowo gotowy',
    failed: 'błąd',
    empty: 'pusty',
  },

  // ── Reguła 59 (23.09): edit zaplanowany albo opublikowany nosi pigułkę publikacji ──
  // Słowo na kaflu i w górnej linii okna podglądu; przy obu stanach „opublikowany”
  // (cięższy wygrywa). Liczba kont stoi wyłącznie w linii okna, jako dana.
  publication: {
    published: 'opublikowany',
    scheduled: 'zaplanowany',
    accounts_one: '{n} konto',
    accounts_few: '{n} konta',
    accounts_many: '{n} kont',
  },

  filters: {
    ready: 'Gotowe',
    queued: 'W kolejce',
    failed: 'Błąd',
  },

  card: {
    // Nie jest samodzielną nazwą — komponent skleja „{untitled} · {data}”
    // (`batchTitle`), więc data jest już na karcie i nie udajemy nadanej nazwy.
    untitled: 'Seria',
    ready: '{done} z {total} gotowych',
    // R5, reguła 30: pigułka serii liczy BŁĘDY. Rzeczownik ZOSTAJE przy liczbie, choć
    // karta jest ciasna: samo „2” obok „2 z 4 gotowych” kazałoby zgadywać, czego
    // dotyczy druga liczba. Pęknięte w przedziale 2–4, czyli w roboczym (batch ma
    // 2–10 editów).
    failed: '{n} błędów',
    failed_one: '{n} błąd',
    failed_few: '{n} błędy',
    failed_many: '{n} błędów',
    open: 'Otwórz serię',
    // Sesja 19, decyzja 16: seria skończona, której użytkownik jeszcze nie obejrzał.
    // Plakietka gaśnie po pierwszej interakcji z KARTĄ (najechanie ≥ 600 ms, fokus,
    // dotknięcie, pobranie, otwarcie serii albo podglądu) — samo wejście na /edits
    // gasi tylko ptaszek w nawigacji, bo to inna obietnica.
    fresh: 'Nowa',
  },

  detail: {
    title: 'Seria - editów: {n}',
    loading: 'Ładowanie serii…',
    error: 'Nie udało się wczytać tej serii.',
    // Audyt 2026-09-04 #21: 404 to odpowiedź, nie awaria — bez „spróbuj ponownie”.
    notFound: {
      title: 'Nie ma takiej serii',
      body: 'Ta seria nie istnieje albo nie należy do Twojego konta.',
      back: 'Wróć do Editów',
    },
    ready: 'gotowych editów',
  },

  job: {
    queued: 'W kolejce',
    rendering: 'W kolejce',
    done: 'Gotowy',
    failed: 'Błąd',
    download: 'Pobierz',
    retry: 'Ponów',
    // {n} = GEMY, nie liczba prób. Cena stoi na przycisku, bo ponowienie
    // pobiera ponownie — użytkownik ma to wiedzieć PRZED kliknięciem.
    //
    // TRZY FORMY, bo cena renderu NIE JEST STAŁA: `render_cost()` daje dziś 1,
    // a po merge'u P01 — 3. Jedna forma byłaby błędem gramatycznym przy którejś
    // z nich („Ponów (1 gemy)" jest dzisiejszą wartością na prodzie). P03 woła
    // `retryCost_${pluralForm(locale, cost)}` (BatchDetail.tsx), więc rodzina
    // jest wymagana, nie kosmetyczna. Baza zostaje do czasu, aż nikt jej nie woła.
    retryCost: 'Ponów ({n} gemów)',
    retryCost_one: 'Ponów ({n} gem)',
    retryCost_few: 'Ponów ({n} gemy)',
    retryCost_many: 'Ponów ({n} gemów)',
    reason: {
      renderFailed: 'Render się nie dokończył',
      // Nieznana klasa błędu mówi to samo co znana, bo dla użytkownika skutek
      // jest identyczny: filmu nie ma, gemy wróciły. Surowy kod nie pomaga.
      unknown: 'Render się nie dokończył',
    },
  },

  empty: {
    title: 'Jeszcze nic tu nie ma',
    cta: 'Zrób swoją pierwszą serię',
  },
}
